/**
 * Motion presets (docs/05-ui-design.md §7, ADR consequences of docs/03 §1 "Motion" row).
 * Components import these by name rather than inventing their own numbers, so every spring in
 * the app feels like the same app.
 */
import type { ReducedMotionConfig, Transition, Variants } from 'motion/react';
import { useEffect, useState } from 'react';

export const spring = {
  /** Buttons, toggles, ticks — quick and slightly overshooting. */
  snappy: { type: 'spring', stiffness: 500, damping: 40, mass: 0.8 } satisfies Transition,
  /** Bucket bars, sheets, anything larger that shouldn't feel jumpy. */
  soft: { type: 'spring', stiffness: 220, damping: 26, mass: 1 } satisfies Transition,
} as const;

export const fade: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

export const slideUp: Variants = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: spring.soft },
  exit: { opacity: 0, y: 24, transition: { duration: 0.15 } },
};

/** Reorder/insert/remove in a list — pair with `<motion.li layout transition={listItem}>`. */
export const listItem: Transition = spring.soft;

/**
 * Insert/remove variants for a list row (docs/05 §7 "Lists"). Pair with `layout="position"` (for
 * reorder — already wired independently) plus a parent `<AnimatePresence>` so `exit` actually
 * plays instead of the item just vanishing. Animates opacity + a small y offset, not height —
 * `layout` on the surrounding rows already animates the space collapsing/opening when a sibling
 * mounts or unmounts, so this only needs to cover the row's own fade in/out.
 */
export const listItemVariants: Variants = {
  initial: { opacity: 0, y: -8 },
  animate: { opacity: 1, y: 0, transition: listItem },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15 } },
};

/**
 * Notification bell shake when an unread count increases while mounted (docs/05 §7
 * "Notification arrives"). A keyframe rotation, not a spring — a bell shake reads as a few quick
 * decaying swings, not a single overshoot-and-settle.
 */
export const bellWiggle = {
  rotate: [0, -14, 11, -7, 4, 0],
  transition: { duration: 0.5, ease: 'easeInOut' },
} satisfies { rotate: number[]; transition: Transition };

/** The unread badge popping in alongside the bell wiggle above. */
export const badgePop = {
  scale: [0, 1.15, 1],
  transition: spring.snappy,
} satisfies { scale: number[]; transition: Transition };

/** Goal reached / EMI last installment — glow bloom on the completed ring (docs/05 §7). */
export const ringGlowBloom = {
  filter: [
    'drop-shadow(0 0 0px currentColor)',
    'drop-shadow(0 0 18px currentColor)',
    'drop-shadow(0 0 4px currentColor)',
  ],
  transition: { duration: 1.1, ease: 'easeOut' },
} satisfies { filter: string[]; transition: Transition };

/**
 * Named durations (seconds) for the one-shot transient effects in docs/05-ui-design.md §7's
 * table — checkbox stroke-draw, row colour flash, section-complete glow pulse, error shake.
 * Kept here rather than inlined at each call site, same reasoning as `spring`/`fade`/`slideUp`.
 */
export const durations = {
  /** "checkbox draws stroke (200 ms)" / its reverse on un-tick. */
  tick: 0.2,
  /** Row background flash to the bucket's colour, then fade back out. */
  flash: 0.5,
  /** Section header glow pulse on completion. */
  pulse: 0.5,
  /** Error / rejected mutation row shake. */
  shake: 0.3,
} as const;

/** Small completion cues live inside the existing toast, so they never move page content. */
export const completionMotion = {
  payment: [
    { opacity: 0.5, transform: 'scale(0.8)' },
    { opacity: 1, transform: 'scale(1.08)', offset: 0.65 },
    { opacity: 1, transform: 'scale(1)' },
  ],
  contribution: [
    { opacity: 0.4, transform: 'translateY(6px)' },
    { opacity: 1, transform: 'translateY(-1px)', offset: 0.7 },
    { opacity: 1, transform: 'translateY(0)' },
  ],
  import: [
    { opacity: 0.4, transform: 'translateX(-4px)' },
    { opacity: 1, transform: 'translateX(0)' },
  ],
  milestone: [
    { opacity: 0.5, transform: 'scale(0.8) rotate(-10deg)' },
    { opacity: 1, transform: 'scale(1.1) rotate(4deg)', offset: 0.65 },
    { opacity: 1, transform: 'scale(1) rotate(0)' },
  ],
} satisfies Record<string, Keyframe[]>;

export const completionTiming: KeyframeAnimationOptions = {
  duration: 320,
  easing: 'cubic-bezier(.22,1,.36,1)',
};

/** View changes remain legible throughout; initial content renders without an entrance delay. */
export const viewChangeTiming: KeyframeAnimationOptions = {
  duration: 160,
  easing: 'cubic-bezier(.22,1,.36,1)',
};

/** Bound celebratory DOM work on phones with less memory/CPU. */
export function celebrationParticleCount(sparkles: boolean, limitedDevice: boolean): number {
  return limitedDevice ? (sparkles ? 12 : 18) : sparkles ? 42 : 72;
}

export type MotionMode = 'system' | 'on' | 'off';

/** What to pass `<MotionConfig reducedMotion>` for each of our three modes. */
export function reducedMotionConfigFor(mode: MotionMode): ReducedMotionConfig {
  if (mode === 'off') return 'always';
  if (mode === 'on') return 'user';
  return 'user'; // defers to prefers-reduced-motion, same as the OS-driven CSS rules
}

/** True when the *effective* result — this mode plus the OS setting — is reduced. */
export function isMotionReduced(mode: MotionMode): boolean {
  if (mode === 'off') return true;

  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Reads the live `data-motion` attribute on `<html>` (set by lib/theme.ts from the preferences
 * store) so components can react to it without needing the store wired all the way down —
 * useful for CountUp/Confetti, which are also meant to work outside an authenticated session.
 */
export function useMotionMode(): MotionMode {
  const read = (): MotionMode => {
    if (typeof document === 'undefined') return 'system';
    const attr = document.documentElement.getAttribute('data-motion');
    return attr === 'on' || attr === 'off' ? attr : 'system';
  };
  const [mode, setMode] = useState<MotionMode>(read);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setMode(read()));
    observer.observe(root, { attributes: true, attributeFilter: ['data-motion'] });
    return () => observer.disconnect();
  }, []);

  return mode;
}

/** Reactive OS + app preference, including changes while an animation is visible. */
export function useEffectiveReducedMotion(): boolean {
  const mode = useMotionMode();
  const [systemReduced, setSystemReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setSystemReduced(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return mode === 'off' || systemReduced;
}
